import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Camera, LoaderCircle, Trash2 } from 'lucide-react';
import { jobHunterService, type JhProfile } from '@/services/job-hunter.service';
import { CandidateAvatar } from '../ui';

/** The candidate's picture: click to upload (JPG / PNG / WebP, 3 MB), stored in ImageKit. */
export function CandidatePhoto({ profile, size = 72, onChange }: { profile: Pick<JhProfile, 'profileUid' | 'name' | 'photo'>; size?: number; onChange: (photo: JhProfile['photo']) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'upload' | 'remove' | null>(null);

  const upload = async (file?: File) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error('Use a JPG, PNG or WebP photo'); return; }
    if (file.size > 3 * 1024 * 1024) { toast.error('The photo must be 3 MB or smaller'); return; }
    setBusy('upload');
    try { const r = await jobHunterService.uploadPhoto(profile.profileUid, file); onChange(r.photo); toast.success('Photo updated'); }
    catch { /* toasted */ } finally { setBusy(null); if (input.current) input.current.value = ''; }
  };
  const remove = async () => {
    if (!window.confirm('Remove the photo?')) return;
    setBusy('remove');
    try { await jobHunterService.removePhoto(profile.profileUid); onChange(null); toast.success('Photo removed'); }
    catch { /* toasted */ } finally { setBusy(null); }
  };

  return (
    <div className="group relative shrink-0" style={{ width: size, height: size }}>
      <input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
      <button type="button" onClick={() => input.current?.click()} disabled={!!busy} title={profile.photo ? 'Change photo' : 'Add a photo'} aria-label={profile.photo ? 'Change photo' : 'Add a photo'}
        className="relative block rounded-full" style={{ width: size, height: size }}>
        <CandidateAvatar name={profile.name} photoUrl={profile.photo?.url} size={size} />
        <span className="absolute inset-0 grid place-items-center rounded-full bg-black/45 text-white opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
          {busy === 'upload' ? <LoaderCircle size={20} className="animate-spin" /> : <Camera size={20} />}
        </span>
        {!profile.photo && !busy && (
          <span className="absolute -bottom-0.5 -right-0.5 grid h-7 w-7 place-items-center rounded-full border-2 border-surface bg-primary text-on-primary shadow-soft"><Camera size={13} /></span>
        )}
      </button>
      {profile.photo && (
        <button type="button" onClick={remove} disabled={!!busy} title="Remove photo" aria-label="Remove photo"
          className="absolute -right-1 -top-1 grid h-6 w-6 place-items-center rounded-full border border-border bg-surface text-text-muted opacity-0 shadow-soft transition hover:text-danger group-hover:opacity-100">
          {busy === 'remove' ? <LoaderCircle size={11} className="animate-spin" /> : <Trash2 size={11} />}
        </button>
      )}
    </div>
  );
}
